import { useState } from 'react'
import { Search, Plus, Edit2, Trash2, Image, Tag, Loader2, ChevronLeft, ChevronRight, X } from 'lucide-react'
import { SupabaseClient } from '@supabase/supabase-js'
import { useBrands, useBrandMutations, Brand } from '@/lib/data'

interface BrandsViewProps {
  client: SupabaseClient | null
}

const OWNERSHIP_TYPES = [
  { value: 'family_owned', label: 'Family Owned', color: 'bg-green-500/10 text-green-400' },
  { value: 'founder_owned', label: 'Founder Owned', color: 'bg-blue-500/10 text-blue-400' },
  { value: 'employee_owned', label: 'Employee Owned', color: 'bg-purple-500/10 text-purple-400' },
  { value: 'cooperative', label: 'Cooperative', color: 'bg-cyan-500/10 text-cyan-400' },
  { value: 'private_equity', label: 'Private Equity', color: 'bg-orange-500/10 text-orange-400' },
  { value: 'private_owned', label: 'Private Owned', color: 'bg-gray-500/10 text-gray-400' },
  { value: 'megacorp', label: 'Megacorp', color: 'bg-red-500/10 text-red-400' },
]

export function BrandsView({ client }: BrandsViewProps) {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [ownershipFilter, setOwnershipFilter] = useState<string>('')
  const [showModal, setShowModal] = useState(false)
  const [editingBrand, setEditingBrand] = useState<Brand | null>(null)
  const limit = 20

  const { brands, total, isLoading, error, refetch } = useBrands(client, {
    page,
    limit,
    search,
    ownershipType: ownershipFilter || undefined,
  })

  const { createBrand, updateBrand, deleteBrand, isLoading: mutationLoading } = useBrandMutations(client)

  const totalPages = Math.ceil(total / limit)

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setSearch(searchInput)
    setPage(1)
  }

  const handleSave = async (formData: Partial<Brand>) => {
    if (editingBrand) {
      await updateBrand(editingBrand.id, formData)
    } else {
      await createBrand(formData)
    }
    setShowModal(false)
    setEditingBrand(null)
    refetch()
  }

  const handleDelete = async (brand: Brand) => {
    if (confirm(`Delete brand "${brand.brand_name}"?`)) {
      await deleteBrand(brand.id)
      refetch()
    }
  }

  const getOwnershipStyle = (type: string | null) => {
    const ownership = OWNERSHIP_TYPES.find(o => o.value === type)
    return ownership?.color || 'bg-[var(--bg-element)] text-[var(--text-muted)]'
  }

  const getOwnershipLabel = (type: string | null) => {
    const ownership = OWNERSHIP_TYPES.find(o => o.value === type)
    return ownership?.label || 'Unknown'
  }

  if (!client) {
    return (
      <div className="flex flex-col items-center justify-center h-[50vh] text-[var(--text-muted)]">
        <Tag size={48} className="mb-4 opacity-20" />
        <p className="text-lg font-medium">Not Connected</p>
        <p className="text-sm">Configure Supabase to view brands</p>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-[var(--text-main)]">Brand Management</h2>
        <button
          onClick={() => {
            setEditingBrand(null)
            setShowModal(true)
          }}
          className="flex items-center gap-2 px-4 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90"
        >
          <Plus size={16} /> Add Brand
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <form onSubmit={handleSearch} className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--text-muted)]" size={18} />
          <input
            type="text"
            placeholder="Search brands..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] pl-10 pr-4 py-2 rounded-lg focus:outline-none focus:border-[var(--primary)]"
          />
        </form>

        <select
          value={ownershipFilter}
          onChange={(e) => {
            setOwnershipFilter(e.target.value)
            setPage(1)
          }}
          className="bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-4 py-2 rounded-lg focus:outline-none focus:border-[var(--primary)]"
        >
          <option value="">All Ownership Types</option>
          {OWNERSHIP_TYPES.map(type => (
            <option key={type.value} value={type.value}>{type.label}</option>
          ))}
        </select>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-lg">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[var(--primary)]" />
          </div>
        ) : brands.length === 0 ? (
          <div className="text-center py-12 text-[var(--text-muted)]">
            No brands found
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--border)]">
                <th className="text-left p-4 text-[var(--text-muted)] font-medium text-sm">Brand</th>
                <th className="text-left p-4 text-[var(--text-muted)] font-medium text-sm">Parent Company</th>
                <th className="text-left p-4 text-[var(--text-muted)] font-medium text-sm">Ownership</th>
                <th className="text-left p-4 text-[var(--text-muted)] font-medium text-sm">Logo</th>
                <th className="text-left p-4 text-[var(--text-muted)] font-medium text-sm">Spotlight</th>
                <th className="text-right p-4 text-[var(--text-muted)] font-medium text-sm">Actions</th>
              </tr>
            </thead>
            <tbody>
              {brands.map((brand) => (
                <tr key={brand.id} className="border-b border-[var(--border)] hover:bg-[var(--bg-element)]">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      {brand.logo_url ? (
                        <img src={brand.logo_url} alt={brand.brand_name} className="w-10 h-10 rounded-lg object-contain bg-white" />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-[var(--bg-element)] flex items-center justify-center">
                          <Tag size={16} className="text-[var(--text-muted)]" />
                        </div>
                      )}
                      <span className="text-[var(--text-main)] font-medium">{brand.brand_name}</span>
                    </div>
                  </td>
                  <td className="p-4 text-[var(--text-muted)]">
                    {brand.parent_company_name || '-'}
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${getOwnershipStyle(brand.ownership_type)}`}>
                      {getOwnershipLabel(brand.ownership_type)}
                    </span>
                  </td>
                  <td className="p-4">
                    {brand.logo_url ? (
                      <span className="text-emerald-400 text-sm">✓ Has logo</span>
                    ) : (
                      <span className="text-[var(--text-muted)] text-sm">Missing</span>
                    )}
                  </td>
                  <td className="p-4">
                    {brand.spotlight_data ? (
                      <span className="text-emerald-400 text-sm">✓ Active</span>
                    ) : (
                      <span className="text-[var(--text-muted)] text-sm">None</span>
                    )}
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => {
                          setEditingBrand(brand)
                          setShowModal(true)
                        }}
                        className="p-2 text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-element)] rounded-lg"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(brand)}
                        className="p-2 text-[var(--text-muted)] hover:text-red-400 hover:bg-red-500/10 rounded-lg"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-[var(--text-muted)] text-sm">
            Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total} brands
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-2 bg-[var(--bg-element)] border border-[var(--border)] rounded-lg disabled:opacity-50"
            >
              <ChevronLeft size={16} className="text-[var(--text-muted)]" />
            </button>
            <span className="text-[var(--text-main)] px-4">Page {page} of {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-2 bg-[var(--bg-element)] border border-[var(--border)] rounded-lg disabled:opacity-50"
            >
              <ChevronRight size={16} className="text-[var(--text-muted)]" />
            </button>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showModal && (
        <BrandModal
          brand={editingBrand}
          onClose={() => {
            setShowModal(false)
            setEditingBrand(null)
          }}
          onSave={handleSave}
          isLoading={mutationLoading}
        />
      )}
    </div>
  )
}

interface BrandModalProps {
  brand: Brand | null
  onClose: () => void
  onSave: (data: Partial<Brand>) => void
  isLoading: boolean
}

function BrandModal({ brand, onClose, onSave, isLoading }: BrandModalProps) {
  const [formData, setFormData] = useState({
    brand_name: brand?.brand_name || '',
    parent_company_name: brand?.parent_company_name || '',
    ownership_type: brand?.ownership_type || '',
    logo_url: brand?.logo_url || '',
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSave({
      brand_name: formData.brand_name,
      parent_company_name: formData.parent_company_name || null,
      ownership_type: (formData.ownership_type || null) as Brand['ownership_type'],
      logo_url: formData.logo_url || null,
      normalized_name: formData.brand_name.toLowerCase().replace(/[^a-z0-9]/g, ''),
    })
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-2xl w-full max-w-lg">
        <div className="p-6 border-b border-[var(--border)] flex items-center justify-between">
          <h2 className="text-xl font-bold text-[var(--text-main)]">
            {brand ? 'Edit Brand' : 'Add Brand'}
          </h2>
          <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text-main)]">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--text-main)] mb-2">Brand Name</label>
            <input
              type="text"
              value={formData.brand_name}
              onChange={(e) => setFormData(prev => ({ ...prev, brand_name: e.target.value }))}
              required
              className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-4 py-2 rounded-lg focus:outline-none focus:border-[var(--primary)]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--text-main)] mb-2">Parent Company</label>
            <input
              type="text"
              value={formData.parent_company_name}
              onChange={(e) => setFormData(prev => ({ ...prev, parent_company_name: e.target.value }))}
              className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-4 py-2 rounded-lg focus:outline-none focus:border-[var(--primary)]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--text-main)] mb-2">Ownership Type</label>
            <select
              value={formData.ownership_type}
              onChange={(e) => setFormData(prev => ({ ...prev, ownership_type: e.target.value }))}
              className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-4 py-2 rounded-lg focus:outline-none focus:border-[var(--primary)]"
            >
              <option value="">Select type...</option>
              {OWNERSHIP_TYPES.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--text-main)] mb-2">Logo URL</label>
            <input
              type="url"
              value={formData.logo_url}
              onChange={(e) => setFormData(prev => ({ ...prev, logo_url: e.target.value }))}
              placeholder="https://..."
              className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-4 py-2 rounded-lg focus:outline-none focus:border-[var(--primary)]"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-[var(--text-muted)] hover:text-[var(--text-main)]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !formData.brand_name}
              className="px-4 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
            >
              {isLoading && <Loader2 size={16} className="animate-spin" />}
              {brand ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
